export type SubService = {
  slug: string;
  title: string;
  short: string;
  body?: string[];
};

export type Service = {
  slug: string;
  title: string;
  navLabel: string;
  short: string;
  hero: string;
  intro: string[];
  bullets?: string[];
  image?: string;
  subservices: SubService[];
};

export const services: Service[] = [
  {
    slug: 'roofing-services',
    title: 'Roofing Services in Essex',
    navLabel: 'Roofing Services',
    short:
      'Effective roofing services delivered by a fully qualified, fully insured team across London, Essex and Suffolk.',
    hero: 'Effective Roofing Services in Essex',
    intro: [
      'Our experienced roofing team is fully qualified and insured to handle every roofing need - from one-off repairs and emergency callouts to full re-roofs, leadwork and protective coatings.',
      'We work on flat roofs, pitched roofs, commercial and domestic properties, with free site surveys and no call-out charges.',
    ],
    bullets: [
      '24/7 emergency roofing across London, Essex & Suffolk',
      'All work fully guaranteed and insured',
      'Free, no-obligation site surveys',
      'Honest written quotes - no hidden extras',
    ],
    subservices: [
      { slug: 'leadwork', title: 'Leadwork', short: 'Specialist lead flashing, valleys and detailing for long-lasting weatherproofing.' },
      { slug: 'dry-ridge-and-verge', title: 'Dry Ridge & Verge', short: 'Mortar-free ridge and verge systems that resist wind, rain and frost.' },
      { slug: 'roof-cleaning-and-coating', title: 'Roof Cleaning & Coating', short: 'Soft wash, moss removal and protective coatings to extend your roof’s life.' },
      { slug: 'rendering', title: 'Rendering', short: 'Modern silicone, monocouche and traditional sand-and-cement renders.' },
      { slug: 'plastering', title: 'Plastering', short: 'Internal and external plastering - skimming, hardwall and patch repairs.' },
      { slug: 'painting', title: 'Painting (Interior & Exterior)', short: 'Decorating and weather-resistant exterior paint systems.' },
      { slug: 'roof-and-wall-coating', title: 'Roof & Wall Coating', short: 'Tile coatings and through-coloured wall coatings for a fresh, protected finish.' },
    ],
  },
  {
    slug: 'new-roofs',
    title: 'New Roofs in Essex',
    navLabel: 'New Roofs',
    short:
      'Full new roof installations: fibreglass, rubber, felt, flat, slate and tiled - built to last decades.',
    hero: 'Brand New Roofs, Built to Last',
    intro: [
      'When repairs are no longer enough, a new roof is one of the most valuable investments you can make in your property.',
      'We install the full range of modern and traditional roof systems - and we’ll help you choose the right one for your home, budget and timeframe.',
    ],
    subservices: [
      { slug: 'fibreglass', title: 'Fibreglass Roofs', short: 'Seamless GRP fibreglass flat roofs with 25-year guarantees.' },
      { slug: 'rubber', title: 'Rubber Roofs (EPDM)', short: 'Single-ply EPDM rubber roofing - durable, low maintenance, long lifespan.' },
      { slug: 'felt', title: 'Felt Roofs', short: 'High-performance torch-on felt for outbuildings, garages and extensions.' },
      { slug: 'flat', title: 'Flat Roofs', short: 'Modern flat roof systems for extensions, dormers and commercial buildings.' },
      { slug: 'slate', title: 'Slate Roofs', short: 'Natural and synthetic slate, fitted by experienced roofers.' },
      { slug: 'tiled', title: 'Tiled Roofs', short: 'Concrete and clay tile roofs in every style, finished to a high standard.' },
    ],
  },
  {
    slug: 'roof-repairs',
    title: 'Roof Repairs in Essex',
    navLabel: 'Roof Repairs',
    short: 'Fast, reliable roof repairs - including 24/7 emergency callouts.',
    hero: 'Reliable Roof Repairs, Day or Night',
    intro: [
      'A small roof problem can become an expensive one quickly. We’ll diagnose the cause, give you an honest written quote and put it right.',
      'Our emergency roofers are on call 24/7 across London, Essex and Suffolk for storm damage, leaks and chimney issues.',
    ],
    subservices: [
      { slug: 'emergency-roofers', title: 'Emergency Roofers', short: '24/7 callouts for leaks, storm damage and missing tiles.' },
      { slug: 'chimney-repairs', title: 'Chimney Repairs', short: 'Repointing, flaunching, leadwork and chimney rebuilds.' },
      { slug: 'ridge-and-tile-repairs', title: 'Ridge & Tile Repairs', short: 'Bedded and dry-ridge re-fixing, slipped tile replacement.' },
    ],
  },
  {
    slug: 'roofline-services',
    title: 'Roofline Services in Essex',
    navLabel: 'Roofline Services',
    short: 'Fascias, soffits, guttering and loft insulation - protecting the edge of your roof.',
    hero: 'Roofline Services',
    intro: [
      'The roofline is the part of your roof that protects everything below it from water and wind. Damaged fascias, soffits and gutters quickly lead to bigger problems - including damp.',
      'We replace, repair and upgrade the full roofline in uPVC, aluminium and traditional timber, and we install loft insulation to bring your home up to modern standards.',
    ],
    subservices: [
      { slug: 'loft-insulation', title: 'Loft Insulation', short: 'Mineral wool and rigid board insulation that cuts heating bills.' },
      { slug: 'fascias-and-soffits', title: 'Fascias & Soffits', short: 'uPVC, aluminium and timber fascias and soffits - full replacements or repairs.' },
      { slug: 'guttering', title: 'Guttering', short: 'New guttering installs, leak repairs, gutter clearing and cleaning.' },
    ],
  },
  {
    slug: 'damp-proofing',
    title: 'Damp Proofing in London, Essex & Suffolk',
    navLabel: 'Damp Proofing',
    short:
      'Specialist damp diagnosis and treatment - penetrating damp, rising damp, condensation and more.',
    hero: 'Damp Proofing London, Essex & Suffolk',
    intro: [
      'Damp is one of the most common - and most damaging - problems in UK homes. We diagnose the underlying cause first, then treat the symptoms with weather-resistant, long-term solutions.',
      'Common causes we put right include broken or missing gutters and downpipes, cracked or missing roof tiles, leaking water pipes, poor ventilation and inadequate drainage.',
    ],
    bullets: [
      'Free damp proofing site surveys',
      'Penetrating, rising and condensation damp',
      'Honest diagnosis - only what you actually need',
      'All work guaranteed',
    ],
    subservices: [],
  },
  {
    slug: 'driveway-services',
    title: 'Driveway Services in Essex',
    navLabel: 'Driveway Services',
    short: 'Block paving, resin bound, gravel, tarmac and imprinted concrete driveways.',
    hero: 'Expert Driveway Installers in Essex',
    intro: [
      'Whether you need a driveway repair or a brand-new driveway installed, we’re here as your local driveway specialists.',
      'We use only the highest-quality materials and finish every drive with carefully designed borders and edging patterns.',
    ],
    subservices: [
      { slug: 'block-paving', title: 'Block Paving', short: 'A classic, hard-wearing finish that adds real kerb appeal.' },
      { slug: 'resin-bound', title: 'Resin Bound', short: 'Smooth, permeable resin surfaces in a wide range of colours.' },
      { slug: 'gravel', title: 'Gravel Driveways', short: 'Low-cost, decorative gravel installations on a properly built base.' },
      { slug: 'tarmac', title: 'Tarmac Driveways', short: 'Fast, durable tarmac surfaces - often installable in a single day.' },
      { slug: 'repairs', title: 'Driveway Repairs', short: 'Sinking, cracking and weed-ridden drives put right.' },
      { slug: 'concrete-bases', title: 'Concrete Bases', short: 'Solid bases for sheds, garages, hot tubs and outbuildings.' },
      { slug: 'imprinted-concrete', title: 'Imprinted Concrete', short: 'Pattern imprinted concrete with the look of paving in one continuous slab.' },
    ],
  },
  {
    slug: 'landscaping',
    title: 'Landscaping in Essex',
    navLabel: 'Landscaping',
    short:
      'Patios, decking, fencing, artificial grass, brickwork, groundworks and drainage - your garden, finished properly.',
    hero: 'Landscaping Contractors in Essex',
    intro: [
      'We’re the number one choice in Essex for garden landscaping - from simple patios and fences to full back-garden transformations.',
      'Our team handles every part of the job in-house: design, groundworks, drainage, hard and soft landscaping.',
    ],
    subservices: [
      { slug: 'artificial-grass', title: 'Artificial Grass', short: 'Lush, low-maintenance lawns that look great year-round.' },
      { slug: 'brickwork', title: 'Brickwork', short: 'Garden walls, retaining walls, feature walls, gate pillars and steps.' },
      { slug: 'groundworks-and-drainage', title: 'Groundworks & Drainage', short: 'Solid foundations and proper drainage for any garden project.' },
      { slug: 'patios', title: 'Patios', short: 'Natural stone, porcelain and concrete patios in any size or shape.' },
      { slug: 'decking', title: 'Decking', short: 'Hardwood, softwood and composite decks built to last.' },
      { slug: 'fencing', title: 'Fencing', short: 'Closeboard, panel, picket and contemporary fencing.' },
    ],
  },
  {
    slug: 'spray-foam-removal',
    title: 'Spray Foam Insulation Removal in Essex',
    navLabel: 'Spray Foam Removal',
    short:
      'Safe, professional spray foam removal - protecting your roof, your mortgage and your property’s value.',
    hero: 'Spray Foam Insulation Removal',
    intro: [
      'Can’t sell your home or remortgage because of spray foam insulation? You’re not alone - and we can help.',
      'Spray foam insulation in roof spaces blocks ventilation, traps moisture, hides structural issues and triggers refusals from most major UK lenders and surveyors.',
      'We remove spray foam insulation safely using dry-ice blasting - no chemicals, no abrasives, and no damage to the underlying timber or felt.',
    ],
    bullets: [
      'Mortgage and valuation barriers removed',
      'Restores roof ventilation and airflow',
      'Eliminates condensation and damp risk',
      'No abrasive damage to roof timbers',
      'Cost-effective and fully insured',
    ],
    subservices: [],
  },
];

export const serviceSlugs = services.map((s) => s.slug);

export const allRoutes = [
  '/',
  '/about',
  '/contact',
  ...services.flatMap((s) => [`/${s.slug}`, ...s.subservices.map((sub) => `/${s.slug}/${sub.slug}`)]),
];

/** Used by both the QuoteForm dropdown and the server-side allowlist. */
export const quoteServiceOptions = services.flatMap((s) => [
  { value: s.slug, label: s.navLabel },
  ...s.subservices.map((sub) => ({
    value: `${s.slug}/${sub.slug}`,
    label: `${s.navLabel} - ${sub.title}`,
  })),
]);

export function findService(slug: string): Service | undefined {
  return services.find((s) => s.slug === slug);
}

export function findSubService(
  serviceSlug: string,
  subSlug: string,
): { service: Service; sub: SubService } | undefined {
  const service = findService(serviceSlug);
  if (!service) return undefined;
  const sub = service.subservices.find((s) => s.slug === subSlug);
  if (!sub) return undefined;
  return { service, sub };
}
